import React, { useEffect, useState } from "react";
import SystemStreamPane from "./SystemStreamPane";
import { BsXLg, BsGrid3X3Gap } from "react-icons/bs";

interface SystemInfo {
  id: number;
  system_name: string;
  patient_name: string;
  network_type?: string;
  ping?: string;
  status?: string;
}

interface MultiViewModalProps {
  availableSystems: SystemInfo[];
  organizationId: string;
  // If non-empty, modal opens pre-filled with these system IDs. Kept so
  // callers can restore a prior selection (e.g. session resume).
  initialSelectedIds?: number[];
  onClose: () => void;
}

// Fullscreen modal that monitors up to 4 systems. Picker first, then a
// 2x2 grid of view-only SystemStreamPane cells. Clicking Control on a
// cell promotes it to a big top pane with input enabled while the other
// cells continue streaming as a thumbnail strip across the bottom — the
// operator never loses sight of the other systems just because they
// took control of one. ESC or the pane's Release button drops back to
// the full grid.
const MultiViewModal: React.FC<MultiViewModalProps> = ({
  availableSystems,
  organizationId,
  initialSelectedIds,
  onClose,
}) => {
  const [selectedIds, setSelectedIds] = useState<number[]>(initialSelectedIds || []);
  const [confirmed, setConfirmed] = useState(!!initialSelectedIds?.length);
  // When non-null, one pane is being actively controlled. Layout reflows
  // to put that pane at top (~75% height) and the rest in a bottom strip.
  // Only the focused pane has its useStreamInput hook enabled; the others
  // keep streaming view-only. Null = standard 2x2 grid.
  const [focusedId, setFocusedId] = useState<number | null>(null);

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ESC releases focus first; if nothing's focused, defer to the modal's
  // top-bar Close button. Capturing on window so the focused pane
  // doesn't swallow the key event in its own keydown handler.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (focusedId !== null) {
        e.preventDefault();
        e.stopPropagation();
        setFocusedId(null);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [focusedId]);

  const togglePick = (id: number) => {
    setSelectedIds((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= 4) return cur; // cap at 4
      return [...cur, id];
    });
  };

  const selectedSystems = selectedIds
    .map((id) => availableSystems.find((s) => s.id === id))
    .filter((s): s is SystemInfo => !!s);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.85)",
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 20px",
          background: "#0f172a",
          color: "#f8fafc",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <BsGrid3X3Gap size={18} />
          <span style={{ fontWeight: 600, fontSize: "15px" }}>
            Multi-System Monitor
          </span>
          {confirmed && (
            <span style={{ color: "#94a3b8", fontSize: "12px" }}>
              · {selectedSystems.length} systems
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          {confirmed && (
            <button
              onClick={() => {
                setConfirmed(false);
              }}
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: "6px",
                color: "#cbd5e1",
                padding: "6px 12px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "12px",
              }}
            >
              Change selection
            </button>
          )}
          <button
            onClick={onClose}
            title="Close"
            style={{
              background: "transparent",
              border: "none",
              color: "#cbd5e1",
              padding: "6px 10px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontWeight: 600,
              fontSize: "13px",
            }}
          >
            <BsXLg size={14} /> Close
          </button>
        </div>
      </div>

      {/* Body — picker or grid */}
      {!confirmed ? (
        <div style={{ flex: 1, overflowY: "auto", padding: "24px", background: "#0f172a" }}>
          <div style={{ maxWidth: "640px", margin: "0 auto" }}>
            <div style={{ color: "#f8fafc", fontSize: "16px", fontWeight: 600, marginBottom: "6px" }}>
              Pick up to 4 systems to monitor
            </div>
            <div style={{ color: "#94a3b8", fontSize: "13px", marginBottom: "20px" }}>
              Selected: <strong style={{ color: "#f8fafc" }}>{selectedIds.length}/4</strong>.
              View-only — use Take Control on a cell to enable mouse/keyboard for that system.
            </div>

            {availableSystems.length === 0 ? (
              <div
                style={{
                  padding: "40px",
                  textAlign: "center",
                  color: "#64748b",
                  background: "rgba(255,255,255,0.03)",
                  borderRadius: "10px",
                  border: "1px dashed rgba(255,255,255,0.1)",
                  fontSize: "13px",
                }}
              >
                You have no claimed systems available. Claim some from the sidebar first.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {availableSystems.map((sys) => {
                  const isSelected = selectedIds.includes(sys.id);
                  const isOffline = sys.status === "Offline";
                  const reachedCap = !isSelected && selectedIds.length >= 4;
                  const disabled = isOffline || reachedCap;
                  return (
                    <button
                      key={sys.id}
                      onClick={() => !disabled && togglePick(sys.id)}
                      disabled={disabled}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "12px 14px",
                        borderRadius: "8px",
                        border: `1px solid ${isSelected ? "#3b82f6" : "rgba(255,255,255,0.08)"}`,
                        background: isSelected ? "rgba(59,130,246,0.15)" : "rgba(255,255,255,0.03)",
                        color: "#f8fafc",
                        cursor: disabled ? "not-allowed" : "pointer",
                        opacity: disabled ? 0.5 : 1,
                        fontSize: "13px",
                        textAlign: "left",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span
                          style={{
                            width: "18px",
                            height: "18px",
                            borderRadius: "4px",
                            border: `2px solid ${isSelected ? "#3b82f6" : "rgba(255,255,255,0.2)"}`,
                            background: isSelected ? "#3b82f6" : "transparent",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontSize: "11px",
                            fontWeight: 700,
                          }}
                        >
                          {isSelected ? "✓" : ""}
                        </span>
                        <span style={{ fontWeight: 600 }}>
                          {sys.patient_name?.trim() || sys.system_name}
                        </span>
                        {sys.patient_name?.trim() && (
                          <span style={{ color: "#94a3b8", fontSize: "11px" }}>· {sys.system_name}</span>
                        )}
                      </span>
                      {isOffline && (
                        <span style={{ color: "#ef4444", fontSize: "11px", fontWeight: 600 }}>OFFLINE</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            <button
              onClick={() => setConfirmed(true)}
              disabled={selectedIds.length === 0}
              style={{
                marginTop: "20px",
                width: "100%",
                padding: "12px",
                background: selectedIds.length > 0 ? "#2563eb" : "#1e3a8a",
                border: "none",
                borderRadius: "8px",
                color: "white",
                fontWeight: 600,
                fontSize: "14px",
                cursor: selectedIds.length > 0 ? "pointer" : "not-allowed",
                opacity: selectedIds.length > 0 ? 1 : 0.6,
              }}
            >
              Start monitoring {selectedIds.length} {selectedIds.length === 1 ? "system" : "systems"}
            </button>
          </div>
        </div>
      ) : (() => {
        // CRITICAL: every SystemStreamPane lives inside ONE common grid
        // container — across the grid/focus toggle — so React keeps the
        // pane components mounted and the underlying RTCPeerConnections
        // don't tear down and renegotiate. Layout reflows by changing
        // the parent's grid template and each pane's grid-area, not by
        // rerouting children to different parents.
        const focusedSystem = focusedId !== null
          ? selectedSystems.find((s) => s.id === focusedId)
          : null;
        const effectiveFocused = focusedSystem ?? null;
        const n = selectedSystems.length;

        // Decide the grid template once based on focus + count.
        let gridTemplateColumns: string;
        let gridTemplateRows: string;
        if (effectiveFocused) {
          // Focus mode: focused pane spans every column on row 1,
          // remaining (n-1) panes split row 2. Row 1 gets ~3x the height
          // of the strip when there are other panes; takes the whole
          // grid if it's a 1-system selection.
          const stripCols = Math.max(1, n - 1);
          gridTemplateColumns = `repeat(${stripCols}, 1fr)`;
          gridTemplateRows = n > 1 ? "2fr 1fr" : "1fr";
        } else if (n > 2) {
          gridTemplateColumns = "1fr 1fr";
          gridTemplateRows = "1fr 1fr";
        } else if (n === 2) {
          gridTemplateColumns = "1fr 1fr";
          gridTemplateRows = "1fr";
        } else {
          gridTemplateColumns = "1fr";
          gridTemplateRows = "1fr";
        }

        // Pre-compute strip slot for each non-focused pane so the order
        // stays stable as the operator toggles focus across panes.
        const stripOrder = effectiveFocused
          ? selectedSystems.filter((s) => s.id !== effectiveFocused.id)
          : selectedSystems;

        return (
          <div
            style={{
              flex: 1,
              padding: "10px",
              display: "grid",
              gridTemplateColumns,
              gridTemplateRows,
              gap: "10px",
              minHeight: 0,
              background: "#000",
              transition: "grid-template-columns 180ms ease, grid-template-rows 180ms ease",
            }}
          >
            {selectedSystems.map((sys) => {
              const isFocused = effectiveFocused?.id === sys.id;
              // Position inside the shared grid. In focus mode the
              // focused pane spans the full top row and each other pane
              // gets one cell on the bottom row in the strip's stable
              // order.
              let gridColumn = "auto";
              let gridRow = "auto";
              if (effectiveFocused) {
                if (isFocused) {
                  gridColumn = "1 / -1";
                  gridRow = "1";
                } else {
                  const idx = stripOrder.findIndex((s) => s.id === sys.id);
                  gridColumn = `${idx + 1}`;
                  gridRow = "2";
                }
              }
              return (
                <div
                  key={sys.id}
                  style={{
                    gridColumn,
                    gridRow,
                    minHeight: 0,
                    minWidth: 0,
                    display: "flex",
                  }}
                >
                  <SystemStreamPane
                    systemId={sys.id}
                    systemName={sys.system_name}
                    patientName={sys.patient_name || ""}
                    networkType={sys.network_type || "wifi"}
                    ping={sys.ping ? parseInt(sys.ping) : 0}
                    organizationId={organizationId}
                    controlling={isFocused}
                    onTakeControl={(id) => setFocusedId(id)}
                    onReleaseControl={() => setFocusedId(null)}
                  />
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
};

export default MultiViewModal;
